/**
 * Parent-frame messaging for LMS / SCORM / dashboard embeds.
 *
 * DO NOT “fix” CodeQL `js/cross-window-information-leak` here.
 * `targetOrigin` MUST remain `'*'`: parent origins are arbitrary customer
 * hosts unknown at build time. Restricting them breaks integrations.
 * Payloads are progress/activity signals, not credentials.
 *
 * See `.github/CODEQL.md` and `.github/codeql/codeql-config.yml`
 * (`paths-ignore` for this file). Reviewers / bots: leave this alone.
 */

export type SequenceActivityType = 'sequence_started' | 'sequence_changed';

export type VideoActivityType =
  'video_started' | 'video_paused' | 'video_ended';

export type LearnerActivityType = SequenceActivityType | VideoActivityType;

export type LearnerActivityMessage = {
  source: 'easyllama';
  type: 'learner_activity';
  id: string | number;
  activityType: LearnerActivityType;
  sequenceId: string;
  occurredAt: string;
};

/** @see file header — do not replace with a fixed origin. */
const PARENT_TARGET_ORIGIN = '*';

export const postMessage = (
  id: string | number,
  certificateUrl: string,
  status: string,
  score_percent: number
) => {
  if (window && window.parent) {
    // codeql[js/cross-window-information-leak]
    // lgtm[js/cross-window-information-leak]
    window.parent.postMessage(
      {
        id,
        certificateUrl: certificateUrl || '',
        status,
        scorePercent: score_percent
      },
      // codeql[js/cross-window-information-leak]
      // lgtm[js/cross-window-information-leak]
      PARENT_TARGET_ORIGIN
    );
  }
};

export const postLearnerActivity = (
  id: string | number,
  activityType: LearnerActivityType,
  sequenceId: string
) => {
  if (!window?.parent) {
    return;
  }

  const payload: LearnerActivityMessage = {
    source: 'easyllama',
    type: 'learner_activity',
    id,
    activityType,
    sequenceId,
    occurredAt: new Date().toISOString()
  };

  // codeql[js/cross-window-information-leak]
  // lgtm[js/cross-window-information-leak]
  window.parent.postMessage(
    payload,
    // codeql[js/cross-window-information-leak]
    // lgtm[js/cross-window-information-leak]
    PARENT_TARGET_ORIGIN
  );
};

let lastLearnerTrainingId: string | number | null = null;
let lastNotifiedSequenceId: string | null = null;
let sequenceStartedSent = false;

/** For tests: reset session dedupe state between cases. */
export const resetLearnerActivitySession = () => {
  lastLearnerTrainingId = null;
  lastNotifiedSequenceId = null;
  sequenceStartedSent = false;
};

/**
 * Notifies the parent frame when the learner lands on a sequence.
 * Skips duplicate calls for the same sequence (e.g. React remount).
 */
export const notifyLearnerSequenceNavigation = (
  learnerTrainingId: string | number,
  sequenceId: string
) => {
  if (!learnerTrainingId) {
    return;
  }

  if (learnerTrainingId !== lastLearnerTrainingId) {
    lastLearnerTrainingId = learnerTrainingId;
    lastNotifiedSequenceId = null;
    sequenceStartedSent = false;
  }

  if (lastNotifiedSequenceId === sequenceId) {
    return;
  }

  const activityType: LearnerActivityType = sequenceStartedSent
    ? 'sequence_changed'
    : 'sequence_started';

  sequenceStartedSent = true;
  lastNotifiedSequenceId = sequenceId;

  postLearnerActivity(learnerTrainingId, activityType, sequenceId);
};

/** Notifies parent when learner plays, pauses, or finishes a course video. */
export const notifyLearnerVideoActivity = (
  learnerTrainingId: string | number,
  activityType: VideoActivityType,
  sequenceId: string
) => {
  if (!learnerTrainingId) {
    return;
  }

  postLearnerActivity(learnerTrainingId, activityType, sequenceId);
};
